import type { ExperienceDefinition } from './ExperienceSwitch.types';

// Identity helper. Exists so each entry is type-checked against ExperienceDefinition at the point of
// declaration (rather than as a whole-object assignment, where a typo names a new experience instead
// of failing), while EXPERIENCES keeps its literal key type for `ExperienceId`.
export const defineExperience = (definition: ExperienceDefinition): ExperienceDefinition => definition;

/**
 * Every "classic vs. new" split that admins can opt between. Mirrors featureFlags.constants.ts:
 * declare once here, consume anywhere by id.
 *
 * A keyed registry (rather than props) is what lets remote surfaces — a tab-nav pill, a dashboard
 * product row — read "is there a new version, and has this user seen it?" without prop-drilling and
 * without an app-wide provider. It also lets one shared Amplitude dashboard split adoption by
 * `experienceId` across every product that adopts.
 */
export const EXPERIENCES = {
  rsvpResponses: defineExperience({
    flagKey: 'rsvpResponsesBetaEnabled',
    // The redesign is the default for the 100% rollout: every admin lands on the new page first and
    // can opt back into the classic page via the banner.
    defaultVersion: 'next',
    // Preserved verbatim from the pre-extraction implementation — renaming these would silently
    // reset every admin's stored preference and un-retire the "New" pill.
    storageKey: 'rsvp:answers:version',
    seenStorageKey: 'rsvp:answers:new-results-seen',
    // The shipped implementation persisted the literal 'beta'. Mapped on read so those admins keep
    // their choice; canonicalized on their next write.
    legacyVersionValues: { beta: 'next' },
    surveyKey: 'rsvpResponses',
    telemetry: { category: 'admin.rsvp.responses', pagePrefix: 'admin' },
  }),
};

export type ExperienceId = keyof typeof EXPERIENCES;

export const getExperienceDefinition = (id: ExperienceId): ExperienceDefinition => EXPERIENCES[id];
